import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Chip,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import PeopleIcon from "@mui/icons-material/People";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import { DataGrid } from "@mui/x-data-grid";
import EmailIcon from "@mui/icons-material/Email";
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ScriptPendingStaff = () => {
  const user = JSON.parse(sessionStorage.getItem("user") || "{}");

  const clientId = user.client_id;
  const collegeId = user.college_id;
  const [sendingEmail, setSendingEmail] = useState(false);
  const [pendingType, setPendingType] = useState("VALUATION");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [staffList, setStaffList] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState("");

  const [totalPending, setTotalPending] = useState(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const sendPendingStaffEmail = async () => {
    try {
      setSendingEmail(true);

      const examCoeEmail = process.env.REACT_APP_EXAM_COE_EMAIL;

      if (!examCoeEmail) {
        alert("Exam COE email is not configured.");
        return;
      }

      const response = await axios.post(
        `${API_BASE_URL}/api/script-valuation/pending-staff/send-email/${clientId}/${collegeId}`,
        {
          pendingType,
          examCoeEmail,
          collegeName: user.college_name || user.collegeName || "College ERP",
        }
      );

      if (response.data?.success) {
        alert(`${pendingType} pending staff report sent successfully to Exam COE`);
      } else {
        alert(response.data?.message || "Failed to send email");
      }
    } catch (error) {
      console.error("sendPendingStaffEmail error:", error);

      alert(error.response?.data?.message || "Failed to send pending staff report");
    } finally {
      setSendingEmail(false);
    }
  };
  const fetchPendingStaff = async () => {
    try {
      setLoading(true);
      setError("");

      setSelectedStaff("");

      const response = await axios.get(
        `${API_BASE_URL}/api/script-valuation/pending-staff/${clientId}/${collegeId}/${pendingType}`
      );

      if (response.data?.success) {
        const data = response.data.data || {};

        setStaffList(data.staff || []);
        setTotalPending(Number(data.total_pending || 0));
      } else {
        setStaffList([]);
        setTotalPending(0);

        setError(response.data?.message || "Unable to fetch pending staff information.");
      }
    } catch (err) {
      console.error("fetchPendingStaff error:", err);

      setStaffList([]);
      setTotalPending(0);

      setError(err.response?.data?.message || "Failed to fetch pending staff information.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId && collegeId) {
      fetchPendingStaff();
    }
  }, [pendingType, clientId, collegeId]);

  const filteredStaffList = useMemo(() => {
    if (!selectedStaff) {
      return staffList;
    }

    return staffList.filter((staff) => String(staff.staff_id) === String(selectedStaff));
  }, [staffList, selectedStaff]);

  const columns = [
    {
      field: "sno",
      headerName: "S.No",
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => params.api.getRowIndexRelativeToVisibleRows(params.id) + 1,
    },
    {
      field: "staff_id",
      headerName: pendingType === "VALUATION" ? "Staff ID" : "Scrutiny Staff ID",
      minWidth: 150,
      flex: 1,
    },
    {
      field: "staff_name",
      headerName: pendingType === "VALUATION" ? "Valuation Staff" : "Scrutiny Staff",
      minWidth: 220,
      flex: 1.5,
    },
    {
      field: "mobile",
      headerName: "Mobile",
      minWidth: 150,
      flex: 1,
    },
    {
      field: "email",
      headerName: "Email",
      minWidth: 220,
      flex: 1.5,
    },
    {
      field: "pending_count",
      headerName: "Pending Count",
      minWidth: 150,
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color="error"
          sx={{
            fontWeight: 700,
          }}
        />
      ),
    },
  ];

  const rows = filteredStaffList.map((staff, index) => ({
    id: String(staff.staff_id),
    sno: index + 1,
    staff_id: staff.staff_id,
    staff_name: staff.staff_name || "-",
    mobile: staff.mobile || "-",
    email: staff.email || "-",
    pending_count: Number(staff.pending_count || 0),
  }));

  return (
    <Box
      sx={{
        width: "100%",
        px: { xs: 1, sm: 2 },
        py: 2,
      }}
    >
      {/* Header */}
      <Card
        sx={{
          width: "100%",
          borderRadius: 2,
          boxShadow: 2,
        }}
      >
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "stretch", sm: "center" },
              gap: 2,
            }}
          >
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "1rem", sm: "1.15rem" },
                }}
              >
                VALUATION AND SCRUTINY PENDING STAFF
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                View staff-wise pending valuation and scrutiny scripts
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Controls */}
      <Card
        sx={{
          mt: 2,
          borderRadius: 2,
          boxShadow: 2,
        }}
      >
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
              alignItems: { xs: "stretch", md: "center" },
            }}
          >
            {/* Pending Type */}
            <TextField
              select
              size="medium"
              label="Pending Type"
              value={pendingType}
              onChange={(e) => {
                setPendingType(e.target.value);
                setSelectedStaff("");
              }}
              InputLabelProps={{
                shrink: true,
              }}
              SelectProps={{
                native: true,
              }}
              sx={{
                minWidth: { md: 220 },
              }}
            >
              <option value="VALUATION">VALUATION</option>
              <option value="SCRUTINY">SCRUTINY</option>
            </TextField>

            {/* Staff */}
            <TextField
              select
              size="medium"
              label={pendingType === "VALUATION" ? "Valuation Staff" : "Scrutiny Staff"}
              value={selectedStaff}
              onChange={(e) => setSelectedStaff(e.target.value)}
              disabled={loading || staffList.length === 0}
              InputLabelProps={{
                shrink: true,
              }}
              SelectProps={{
                native: true,
              }}
              sx={{
                minWidth: { md: 350 },
              }}
            >
              <option value="">All Staff</option>

              {staffList.map((staff) => (
                <option key={String(staff.staff_id)} value={String(staff.staff_id)}>
                  {staff.staff_name || "Unknown Staff"} - Pending: {staff.pending_count}
                </option>
              ))}
            </TextField>
            <Button
              variant="outlined"
              startIcon={loading ? <CircularProgress size={18} /> : <RefreshIcon />}
              onClick={fetchPendingStaff}
              disabled={loading}
              sx={{
                minWidth: { xs: "100%", sm: 120 },
                backgroundColor: "#1976d2",
                color: "#fff",
                "&:hover": { backgroundColor: "#4f95da" },
                borderRadius: "8px",
                fontSize: "14px",
                padding: "4px 12px",
                textTransform: "none",
              }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={
                sendingEmail ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <EmailIcon />
              }
              onClick={sendPendingStaffEmail}
              disabled={sendingEmail || loading}
              sx={{
                minWidth: { xs: "100%", sm: 180 },
                backgroundColor: "#1976d2",
                color: "#fff",
                "&:hover": { backgroundColor: "#4f95da" },
                borderRadius: "8px",
                fontSize: "14px",
                padding: "4px 12px",
                textTransform: "none",
              }}
            >
              {sendingEmail ? "Sending..." : "Send to Exam COE"}
            </Button>
            {/* Total Count */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                minWidth: { md: 220 },
                whiteSpace: "nowrap",
              }}
            >
              <PeopleIcon color="primary" />

              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                }}
              >
                {pendingType === "VALUATION" ? "Valuation Staff" : "Scrutiny Staff"}
              </Typography>

              <Chip
                label={staffList.length}
                color="error"
                size="small"
                sx={{
                  fontWeight: 700,
                }}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                minWidth: { md: 220 },
              }}
            >
              <PendingActionsIcon color="warning" />

              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                }}
              >
                {pendingType === "VALUATION" ? "Valuation Pending" : "Scrutiny Pending"}
              </Typography>

              <Chip
                label={totalPending}
                color="error"
                size="small"
                sx={{
                  fontWeight: 700,
                }}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <Alert
          severity="error"
          sx={{
            mt: 2,
          }}
        >
          {error}
        </Alert>
      )}

      {/* DataGrid */}
      <Card
        sx={{
          mt: 2,
          borderRadius: 2,
          boxShadow: 2,
          width: "100%",
        }}
      >
        <CardContent
          sx={{
            p: { xs: 1, sm: 2 },
            "&:last-child": {
              pb: { xs: 1, sm: 2 },
            },
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              mb: 1.5,
            }}
          >
            {pendingType === "VALUATION"
              ? "Valuation Pending Staff List"
              : "Scrutiny Pending Staff List"}
          </Typography>

          <Box
            sx={{
              width: "100%",
              overflowX: "auto",
            }}
          >
            <DataGrid
              autoHeight
              rows={rows}
              columns={columns}
              loading={loading}
              disableSelectionOnClick
              pagination
              page={page}
              onPageChange={(newPage) => setPage(newPage)}
              pageSize={pageSize}
              onPageSizeChange={(newPageSize) => {
                setPageSize(newPageSize);
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              disableColumnMenu={false}
              sx={{
                minWidth: 850,

                "& .MuiDataGrid-columnHeaders": {
                  fontWeight: 600,
                },

                "& .MuiDataGrid-cell": {
                  fontSize: {
                    xs: "0.8rem",
                    sm: "0.875rem",
                  },
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ScriptPendingStaff;
